import { TranscriptionEvents } from '../events.js'

/**
 * AbstractAdapter - Defines the interface that all transcription adapters must implement
 */
export class AbstractAdapter {
  constructor(config = {}) {
    this.config = { ...config }
    this.events = new TranscriptionEvents()
    this.connectionState = 'disconnected'
    this.isConnected = false
  }

  // Abstract methods that must be implemented by subclasses

  /**
   * Connect to the transcription service
   * @param {Function} getMediaStream Function that returns Promise<MediaStream>
   * @returns {Promise<void>}
   * @abstract
   */
  async connect(getMediaStream) {
    throw new Error('connect() must be implemented by subclass')
  }

  /**
   * Disconnect from the transcription service
   * @returns {Promise<void>}
   * @abstract
   */
  async disconnect() {
    throw new Error('disconnect() must be implemented by subclass')
  }

  /**
   * Parse response from the transcription service
   * @param {*} response Raw response from the service
   * @returns {Object} Parsed response with standardized format
   */
  parseResponse(response) {
    // Default implementation - subclasses should override
    return {
      text: response.text || '',
      isFinal: response.isFinal || false,
      confidence: response.confidence || 1.0,
    }
  }

  // Event handling methods

  /**
   * Add an event listener
   * @param {string} event Event name
   * @param {Function} callback Callback function
   */
  on(event, callback) {
    this.events.on(event, callback)
  }

  /**
   * Remove an event listener
   * @param {string} event Event name
   * @param {Function} callback Callback function
   */
  off(event, callback) {
    this.events.off(event, callback)
  }

  /**
   * Emit an event
   * @param {string} event Event name
   * @param {*} data Data to emit
   */
  emit(event, data) {
    this.events.emit(event, data)
  }

  // State management methods

  /**
   * Check if adapter is connected
   * @returns {boolean}
   */
  isConnected() {
    return this.isConnected
  }

  /**
   * Get current connection state
   * @returns {string} Connection state ('disconnected', 'connecting', 'connected', 'error')
   */
  getConnectionState() {
    return this.connectionState
  }

  /**
   * Set connection state and emit events
   * @param {string} state New connection state
   */
  setConnectionState(state) {
    const previousState = this.connectionState
    this.connectionState = state
    this.isConnected = state === 'connected'

    // Emit connection events
    if (this.isConnected && previousState !== 'connected') {
      this.emit(TranscriptionEvents.EVENTS.CONNECTION_ESTABLISHED)
    } else if (!this.isConnected && previousState === 'connected') {
      this.emit(TranscriptionEvents.EVENTS.CONNECTION_LOST)
    }
  }

  // Utility methods

  /**
   * Handle errors and emit error events
   * @param {Error|string} error Error to handle
   */
  handleError(error) {
    const errorObj = error instanceof Error ? error : new Error(error)

    console.error('Transcription adapter error:', errorObj)

    this.setConnectionState('error')
    this.emit(TranscriptionEvents.EVENTS.TRANSCRIPTION_ERROR, errorObj)
  }

  /**
   * Cleanup resources (called during disconnect)
   */
  cleanup() {
    this.events.removeAllListeners()
    this.setConnectionState('disconnected')
  }
}